import React from 'react';
import type { EvaluationItem, MultipleChoiceConfig, OptionDef } from '../../../../../types/course';
import FloatingLabelPill from '../shared/FloatingLabelPill';
import AnswerPill from '../shared/AnswerPill';
import SectionHeader from '../shared/SectionHeader';
import AddItemCTA from '../shared/AddItemCTA';
import TrashButton from '../shared/TrashButton';
import { uid } from '../shared/tokens';

interface ItemEditorProps {
  item: Extract<EvaluationItem, { type: 'multiple_choice' }>;
  onChange: (patch: Partial<Extract<EvaluationItem, { type: 'multiple_choice' }>>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

export default function MultipleChoiceItem({ item, onChange, onDelete, readOnly }: ItemEditorProps) {
  const { question, options, correct_answers } = item.config;

  const updateConfig = (patch: Partial<MultipleChoiceConfig>) => {
    onChange({ config: { ...item.config, ...patch } });
  };

  const updateOption = (id: string, text: string) => {
    updateConfig({ options: options.map((o) => (o.id === id ? { ...o, text } : o)) });
  };

  const toggleCorrect = (id: string) => {
    const next = correct_answers.includes(id)
      ? correct_answers.filter((x) => x !== id)
      : [...correct_answers, id];
    updateConfig({ correct_answers: next });
  };

  const removeOption = (id: string) => {
    if (options.length <= 2) return;
    updateConfig({
      options: options.filter((o) => o.id !== id),
      correct_answers: correct_answers.filter((x) => x !== id),
    });
  };

  const addOption = () => {
    const newOption: OptionDef = { id: uid('opt'), text: '' };
    updateConfig({ options: [...options, newOption] });
  };

  return (
    <div className="flex flex-col gap-[31px] w-full">
      {/* Multiple correct answers need no mode toggle — selecting several
          answers as correct is the default behaviour. */}
      <SectionHeader title="Multiple Choice Question" />

      <FloatingLabelPill
        label="Question"
        value={question}
        onChange={(v) => updateConfig({ question: v })}
        placeholder="What is the question?"
        size="xl"
        readOnly={readOnly}
        trailingIcon={!readOnly && <TrashButton onClick={onDelete} ariaLabel="Delete question" />}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-[18px]">
        {options.map((opt) => (
          <AnswerPill
            key={opt.id}
            value={opt.text}
            onChange={(v) => updateOption(opt.id, v)}
            placeholder="Type an answer…"
            state={correct_answers.includes(opt.id) ? 'selected' : 'default'}
            onToggleCorrect={() => toggleCorrect(opt.id)}
            onRemove={options.length > 2 ? () => removeOption(opt.id) : undefined}
            readOnly={readOnly}
          />
        ))}
      </div>

      {!readOnly && <AddItemCTA label="Add Answer" onClick={addOption} />}
    </div>
  );
}
